import { api, ApiRequestError } from '../../lib/api';
import { ACCENTS, isAccentName } from '@shared/branding';
import type { ActivePageResponse, PublicPageResponse, PublicProvider, PublicService } from '../types';

export type PageLoad =
  | { kind: 'ready'; data: PublicPageResponse }
  | { kind: 'not_found' }
  | { kind: 'disabled'; message: string }
  | { kind: 'error'; message: string };

/** The path the HTML was served for; the inline JSON belongs to it only. */
const initialPath = typeof window !== 'undefined' ? window.location.pathname : '';
let inline: { slug: string; pageSlug: string | null; data: PublicPageResponse } | null | undefined;

/** The industry page the HTML was served for, if it was served for one. */
function segment(path: string, index: number): string {
  return decodeURIComponent(path.replace(/^\/+/, '').split('/')[index] ?? '');
}

/**
 * The Worker injects `<script id="booking-page" type="application/json">`
 * with the same JSON as GET /api/public/:slug into the HTML for /:slug, so
 * the first paint needs no round trip. Used once, for that page only.
 *
 * "That page" includes the industry page: /:slug and /:slug/photography
 * are the same business but different service lists, so a payload minted
 * for one must never be handed to the other.
 */
function readInline(slug: string, pageSlug: string | null): PublicPageResponse | null {
  if (inline === undefined) {
    inline = null;
    const el = document.getElementById('booking-page');
    const initialSlug = segment(initialPath, 0);
    const second = segment(initialPath, 1);
    const initialPageSlug = second && second !== 'appointments' && second !== 'shop' ? second : null;
    if (el && el.tagName === 'SCRIPT' && initialSlug) {
      try {
        const parsed = JSON.parse(el.textContent ?? '') as unknown;
        if (parsed && typeof parsed === 'object' && 'status' in parsed) {
          const data = parsed as PublicPageResponse;
          const orgSlug = data.status === 'active' ? data.organization?.slug : undefined;
          if (!orgSlug || orgSlug === initialSlug) inline = { slug: initialSlug, pageSlug: initialPageSlug, data };
        }
      } catch {
        inline = null;
      }
      el.remove();
    }
  }
  return inline && inline.slug === slug && inline.pageSlug === pageSlug ? inline.data : null;
}

export async function loadBookingPage(slug: string, pageSlug: string | null = null): Promise<PageLoad> {
  const inline = readInline(slug, pageSlug);
  if (inline) return { kind: 'ready', data: inline };
  try {
    const path = pageSlug
      ? `/public/${encodeURIComponent(slug)}/pages/${encodeURIComponent(pageSlug)}`
      : `/public/${encodeURIComponent(slug)}`;
    const data = await api<PublicPageResponse>(path);
    return { kind: 'ready', data };
  } catch (e) {
    if (e instanceof ApiRequestError) {
      if (e.status === 404) return { kind: 'not_found' };
      if (e.code === 'feature_disabled') return { kind: 'disabled', message: e.message };
      return { kind: 'error', message: e.message };
    }
    return { kind: 'error', message: 'We could not load this page. Please check your connection and try again.' };
  }
}

/** Sets --accent / --accent-soft on the document from the page's accent name. */
export function applyAccent(accent: string | null | undefined): void {
  const a = isAccentName(accent) ? ACCENTS[accent] : ACCENTS.blue;
  const root = document.documentElement.style;
  root.setProperty('--accent', a.color);
  root.setProperty('--accent-soft', a.soft);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', a.color);
}

/** Providers who offer a service (optionally at one location). */
export function providersFor(page: ActivePageResponse, service: PublicService, locationId?: string | null): PublicProvider[] {
  return page.providers.filter(
    (p) => p.service_ids.includes(service.id) && (!locationId || p.location_id === locationId),
  );
}

/** Public locations where at least one provider offers the service. */
export function locationsFor(page: ActivePageResponse, service: PublicService) {
  const ids = new Set(providersFor(page, service).map((p) => p.location_id));
  for (const id of service.location_ids ?? []) ids.add(id);
  return page.locations.filter((l) => ids.has(l.id));
}
